import { useMemo, useRef, useState } from 'react';
import {
  maakTekstItem,
  secondeNaarFrame,
  volgendVrijFrame,
  type ProjectModel,
  type TrackType,
} from '@video-studio/shared';
import { api, ApiFoutException, type MediaDto } from '../../api/client';
import { sleepTypeVoorMedia, zetMediaOpTijdlijn } from '../../state/plaatsMedia';
import type { useEditor } from '../../state/useEditor';
import { AiPaneel } from './AiPaneel';
import { TemplatePaneel } from './TemplatePaneel';
import { ChatPaneel } from './ChatPaneel';
import { OndertitelPaneel } from './OndertitelPaneel';

export type ZijpaneelTab = 'media' | 'tekst' | 'audio' | 'templates' | 'ai' | 'montage';

export interface MediapaneelProps {
  editor: ReturnType<typeof useEditor>;
  actief: ZijpaneelTab;
  onWissel: (tab: ZijpaneelTab) => void;
  opFrame: number;
  naarFrame: (frame: number) => void;
  duurInFrames: number;
}

const TABS: Array<{ id: ZijpaneelTab; label: string; icoon: string }> = [
  { id: 'media', label: 'Media', icoon: '🎞' },
  { id: 'tekst', label: 'Tekst', icoon: 'T' },
  { id: 'audio', label: 'Audio', icoon: '♪' },
  { id: 'templates', label: 'Templates', icoon: '▦' },
  { id: 'ai', label: 'AI-video', icoon: '✨' },
  { id: 'montage', label: 'Montage', icoon: '💬' },
];

export function Mediapaneel({ editor, actief, onWissel, opFrame, naarFrame, duurInFrames }: MediapaneelProps) {
  return (
    <aside className="paneel paneel--links">
      <nav className="paneel__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`paneel__tab ${actief === tab.id ? 'paneel__tab--actief' : ''}`}
            onClick={() => onWissel(tab.id)}
            title={tab.label}
          >
            <span className="paneel__tabicoon">{tab.icoon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      <div className="paneel__inhoud">
        {actief === 'media' && <MediaTab editor={editor} opFrame={opFrame} naarFrame={naarFrame} />}
        {actief === 'tekst' && <TekstTab editor={editor} opFrame={opFrame} />}
        {actief === 'audio' && <AudioTab editor={editor} opFrame={opFrame} naarFrame={naarFrame} />}
        {actief === 'templates' && <TemplatePaneel editor={editor} />}
        {actief === 'ai' && <AiPaneel editor={editor} />}
        {actief === 'montage' && (
          <div className="montagetab">
            <OndertitelPaneel editor={editor} />
            <hr className="scheiding" />
            <ChatPaneel editor={editor} />
          </div>
        )}
      </div>
    </aside>
  );
}

/** Bepaalt waar een nieuw item op een track komt te staan. */
function vrijeStart(project: ProjectModel, type: TrackType, duur: number, voorkeur: number): number {
  const track = project.tracks.find((t) => t.type === type);
  if (!track) return Math.max(0, voorkeur);
  // Als de voorkeurspositie vrij is, gebruiken we die; anders schuiven we door.
  const bezet = track.items.some(
    (i) => voorkeur < i.startFrame + i.duurInFrames && i.startFrame < voorkeur + duur,
  );
  if (!bezet) return Math.max(0, voorkeur);
  return volgendVrijFrame(track, duur);
}

function MediaTab({
  editor,
  opFrame,
  naarFrame,
}: {
  editor: ReturnType<typeof useEditor>;
  opFrame: number;
  naarFrame: (frame: number) => void;
}) {
  const [bezigMetUpload, setBezigMetUpload] = useState<{ naam: string; voortgang: number } | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [slepen, setSlepen] = useState(false);
  const invoerRef = useRef<HTMLInputElement>(null);
  const { media, mediaMap } = editor;

  const videoMedia = useMemo(() => media.filter((m) => m.soort === 'video'), [media]);
  const beeldMedia = useMemo(
    () => media.filter((m) => m.soort === 'afbeelding' || m.mimeType.startsWith('image/')),
    [media],
  );
  const audioMedia = useMemo(() => media.filter((m) => m.soort === 'audio'), [media]);

  const upload = async (bestanden: FileList | File[]) => {
    setFout(null);
    for (const bestand of Array.from(bestanden)) {
      const soort: 'video' | 'afbeelding' | 'audio' = bestand.type.startsWith('video/')
        ? 'video'
        : bestand.type.startsWith('audio/')
          ? 'audio'
          : 'afbeelding';
      setBezigMetUpload({ naam: bestand.name, voortgang: 0 });
      try {
        const resultaat = await api.uploadMedia(bestand, soort, (f) =>
          setBezigMetUpload({ naam: bestand.name, voortgang: f }),
        );
        if (resultaat.melding) setFout(resultaat.melding);
        await editor.herlaadMedia();
      } catch (f) {
        setFout(
          f instanceof ApiFoutException
            ? f.message
            : `Uploaden van ${bestand.name} is mislukt.`,
        );
      } finally {
        setBezigMetUpload(null);
      }
    }
  };

  const voegToe = (item: MediaDto) => {
    zetMediaOpTijdlijn(editor, item, opFrame, naarFrame);
  };

  return (
    <div
      className={`mediatab ${slepen ? 'mediatab--slepen' : ''}`}
      onDragOver={(e) => {
        // Alleen echte bestanden van de computer zijn een upload. Een kaart uit
        // de bibliotheek gaat naar de tijdlijn en mag dit vlak niet claimen.
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setSlepen(true);
      }}
      onDragLeave={() => setSlepen(false)}
      onDrop={(e) => {
        e.preventDefault();
        setSlepen(false);
        if (e.dataTransfer.files.length > 0) void upload(e.dataTransfer.files);
      }}
    >
      <div className="mediatab__upload">
        <input
          ref={invoerRef}
          type="file"
          accept="video/*,image/*,audio/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) void upload(e.target.files);
            e.target.value = '';
          }}
        />
        <button type="button" className="knop knop--primair knop--breed" onClick={() => invoerRef.current?.click()}>
          Bestanden uploaden
        </button>
        <p className="mediatab__hint">
          Sleep bestanden hierheen of klik hierboven. Zet een video daarna op de tijdlijn met de
          oranje knop, of sleep de kaart naar de tijdlijn of het beeld.
        </p>
        {bezigMetUpload && (
          <div className="voortgangsbalk">
            <div className="voortgangsbalk__vulling" style={{ width: `${bezigMetUpload.voortgang * 100}%` }} />
            <span>
              {bezigMetUpload.naam} — {Math.round(bezigMetUpload.voortgang * 100)}%
            </span>
          </div>
        )}
        {fout && <div className="melding melding--waarschuwing melding--klein">{fout}</div>}
      </div>

      <Mediagroep
        titel="Video"
        items={videoMedia}
        leeg="Nog geen video geüpload."
        onToevoegen={voegToe}
        editor={editor}
        toonDuur
      />
      <Mediagroep
        titel="Afbeeldingen en logo's"
        items={beeldMedia}
        leeg="Nog geen afbeeldingen. Transparante PNG's werken als logo."
        onToevoegen={voegToe}
        editor={editor}
      />
      {audioMedia.length > 0 && (
        <Mediagroep
          titel="Audio"
          items={audioMedia}
          leeg=""
          onToevoegen={voegToe}
          editor={editor}
        />
      )}
      <p className="mediatab__telling">{mediaMap.size} bestanden in de bibliotheek</p>
    </div>
  );
}

function Mediagroep({
  titel,
  items,
  leeg,
  onToevoegen,
  editor,
  toonDuur,
}: {
  titel: string;
  items: MediaDto[];
  leeg: string;
  onToevoegen: (item: MediaDto) => void;
  editor: ReturnType<typeof useEditor>;
  toonDuur?: boolean;
}) {
  return (
    <section className="mediagroep">
      <h3>{titel}</h3>
      {items.length === 0 ? (
        leeg ? <p className="mediagroep__leeg">{leeg}</p> : null
      ) : (
        <ul className="medialijst">
          {items.map((item) => {
            const klaar = item.inspectieStatus === 'klaar';
            return (
            <li
              key={item.id}
              className={`mediakaart ${klaar ? 'mediakaart--sleepbaar' : ''}`}
              draggable={klaar}
              title={klaar ? 'Sleep naar de tijdlijn of het beeld' : 'Wacht tot de controle klaar is'}
              onDragStart={(e) => {
                const sleepType = sleepTypeVoorMedia(item);
                if (!klaar || !sleepType) {
                  e.preventDefault();
                  return;
                }
                e.dataTransfer.setData(sleepType, item.id);
                e.dataTransfer.effectAllowed = 'copy';
              }}
            >
              <div className="mediakaart__beeld">
                {item.soort === 'video' ? (
                  <video src={item.proxyUrl ?? item.bronUrl} muted preload="metadata" draggable={false} />
                ) : item.soort === 'audio' ? (
                  <span className="mediakaart__audio">♪</span>
                ) : (
                  <img src={item.bronUrl} alt="" draggable={false} />
                )}
                {item.inspectieStatus !== 'klaar' && (
                  <span className={`mediakaart__status mediakaart__status--${item.inspectieStatus}`}>
                    {item.inspectieStatus === 'mislukt' ? 'Niet ondersteund' : 'Controleren…'}
                  </span>
                )}
              </div>
              <div className="mediakaart__acties">
                <button
                  type="button"
                  className="knop knop--primair knop--klein"
                  disabled={!klaar}
                  onClick={() => onToevoegen(item)}
                  title={klaar ? 'Op de tijdlijn zetten' : 'Wacht tot de controle klaar is'}
                >
                  Op tijdlijn
                </button>
                <button
                  type="button"
                  className="knop knop--klein knop--gevaar"
                  draggable={false}
                  onClick={async () => {
                    if (!confirm(`"${item.naam}" definitief verwijderen?`)) return;
                    await api.verwijderMedia(item.id);
                    await editor.herlaadMedia();
                  }}
                  title="Uit de bibliotheek verwijderen"
                >
                  ✕
                </button>
              </div>
              <div className="mediakaart__info">
                <span className="mediakaart__naam" title={item.naam}>
                  {item.naam}
                </span>
                <span className="mediakaart__meta">
                  {item.inspectie?.breedte && item.inspectie?.hoogte
                    ? `${item.inspectie.breedte}×${item.inspectie.hoogte} · `
                    : ''}
                  {toonDuur && item.inspectie?.duurSeconden
                    ? `${item.inspectie.duurSeconden.toFixed(1)} s · `
                    : ''}
                  {(item.grootteBytes / (1024 * 1024)).toFixed(1)} MB
                </span>
                {item.inspectieFout && (
                  <span className="mediakaart__fout">{item.inspectieFout}</span>
                )}
                {item.inspectie?.waarschuwingen?.map((w) => (
                  <span key={w} className="mediakaart__waarschuwing">
                    {w}
                  </span>
                ))}
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TekstTab({ editor, opFrame }: { editor: ReturnType<typeof useEditor>; opFrame: number }) {
  const [tekst, setTekst] = useState('');
  const [stijl, setStijl] = useState<'titel' | 'ondertitel' | 'kader'>('titel');

  const voegToe = () => {
    const project = editor.project;
    if (!project) return;
    const sjablonen = {
      titel: { fontSize: 84, fontWeight: 800, y: 0.5, maxBreedte: 0.85, animatie: 'omhoog' as const },
      ondertitel: { fontSize: 48, fontWeight: 600, y: 0.85, maxBreedte: 0.9, animatie: 'geen' as const },
      kader: { fontSize: 56, fontWeight: 700, y: 0.2, maxBreedte: 0.7, animatie: 'vervagen' as const },
    };
    const gekozen = sjablonen[stijl];
    const duur = secondeNaarFrame(3, project.fps);
    const start = vrijeStart(project, 'tekst', duur, opFrame);
    editor.bewerk(
      (p) => ({
        ...p,
        tracks: p.tracks.map((t) =>
          t.type === 'tekst'
            ? {
                ...t,
                items: [
                  ...t.items,
                  maakTekstItem({
                    startFrame: start,
                    duurInFrames: duur,
                    tekst: tekst.trim() || 'Nieuwe tekst',
                    naam: tekst.trim().slice(0, 30) || 'Tekst',
                    ...gekozen,
                  }),
                ],
              }
            : t,
        ),
      }),
      'Tekst toevoegen',
    );
    setTekst('');
  };

  return (
    <div className="teksttab">
      <h3>Tekst toevoegen</h3>
      <label className="veld">
        <span>Tekst</span>
        <textarea
          rows={3}
          value={tekst}
          placeholder="Bijvoorbeeld: Bekijk ons assortiment"
          onChange={(e) => setTekst(e.target.value)}
        />
      </label>
      <div className="veld">
        <span>Stijl</span>
        <div className="keuzeknoppen">
          {(
            [
              { id: 'titel', label: 'Titel' },
              { id: 'ondertitel', label: 'Ondertitel' },
              { id: 'kader', label: 'Kader' },
            ] as const
          ).map((optie) => (
            <button
              key={optie.id}
              type="button"
              className={`knop knop--klein ${stijl === optie.id ? 'knop--actief' : ''}`}
              onClick={() => setStijl(optie.id)}
            >
              {optie.label}
            </button>
          ))}
        </div>
      </div>
      <button type="button" className="knop knop--primair knop--breed" onClick={voegToe}>
        Op de tijdlijn zetten
      </button>
      <p className="paneel__hint">
        De tekst komt op de playhead te staan. Daarna pas je lettertype, grootte, kleur en tijd aan
        via het paneel rechts.
      </p>
    </div>
  );
}

function AudioTab({
  editor,
  opFrame,
  naarFrame,
}: {
  editor: ReturnType<typeof useEditor>;
  opFrame: number;
  naarFrame: (frame: number) => void;
}) {
  const audio = editor.media.filter((m) => m.soort === 'audio');
  return (
    <div className="audiotab">
      <h3>Audiobronnen</h3>
      {audio.length === 0 ? (
        <p className="paneel__hint">
          Nog geen audio geüpload. Ga naar Media en upload een MP3 of M4A. Bij het toevoegen wordt de
          muziek standaard op 40% volume gezet.
        </p>
      ) : (
        <ul className="medialijst">
          {audio.map((item) => {
            const klaar = item.inspectieStatus === 'klaar';
            return (
            <li
              key={item.id}
              className={`audiokaart ${klaar ? 'mediakaart--sleepbaar' : ''}`}
              draggable={klaar}
              title={klaar ? 'Sleep naar de tijdlijn of het beeld' : 'Wacht tot de controle klaar is'}
              onDragStart={(e) => {
                const sleepType = sleepTypeVoorMedia(item);
                if (!klaar || !sleepType) {
                  e.preventDefault();
                  return;
                }
                e.dataTransfer.setData(sleepType, item.id);
                e.dataTransfer.effectAllowed = 'copy';
              }}
            >
              <span className="audiokaart__naam">{item.naam}</span>
              <span className="audiokaart__meta">
                {item.inspectie?.duurSeconden ? `${item.inspectie.duurSeconden.toFixed(1)} s` : 'onbekend'} ·{' '}
                {item.inspectie?.audiocodec ?? '?'}
              </span>
              <button
                type="button"
                className="knop knop--primair knop--klein knop--breed"
                disabled={!klaar}
                onClick={() => zetMediaOpTijdlijn(editor, item, opFrame, naarFrame)}
              >
                Op tijdlijn
              </button>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
